import { IHttpService, IPromise, IQService } from 'angular';
import HttpService from '../../common/services/http.service';
import { PackageThumbnail, Payment } from '../../common/services/package/package.model';


/* @ngInject */
export class MasterpassService {
  private masterConfig;
  private version: string;
  private token: string = '';
  constructor(
    private $http: IHttpService,
    private $q: IQService,
    private httpService: HttpService,
    private config,
    private i18n
  ) {
    this.masterConfig = config.masterpass;
    this.initializeMasterpassSdk();
  }

  private initializeMasterpassSdk(): void {
    const endpoint = this.masterConfig.sdkEndpoint;
    const masterpass = (window as any)['Masterpass'];
    if (!!masterpass && !!endpoint) {
      masterpass.setEndpoint(endpoint);
    }
  }

  public getVersion(): IPromise<string> {
    if (!!this.version) {
      return this.$q.when(this.version);
    }

    return this.httpService.extractResponseResult(
      this.$http.get(`hafifmuzik/mobile/masterpass/version`)
    ).then(version => {
      this.version = version === 'v1' ? 'v1' : 'v2';
      return this.version;
    });
  }

  public isV2(): IPromise<boolean> {
    return this.getVersion().then(version => version === 'v2');
  }

  public callV2WithFreshToken<T>(action: (token: string) => IPromise<T>): IPromise<T> {
    return this.ensureToken().then(token => this.executeWithTokenRetry<T>(action, token));
  }

  private executeWithTokenRetry<T>(action: (token: string) => IPromise<T>, token: string): IPromise<T> {
    return action(token).then(
      response => response,
      error => {
        const errorCode = this.getMasterpassExceptionCode(error);
        const isAuthError = error && (error.statusCode === 401 || error.statusCode === 403 || errorCode === 'TOKEN_HAS_EXPIRED' || errorCode === 'TOKEN_IS_NOT_VALID' || errorCode === 'AUTHENTICATION_FAILED');

        if (isAuthError) {
          return this.getTokenV2().then(
            freshToken => {
              this.token = freshToken;
              return action(freshToken);
            },
            _tokenError => {
              return this.$q.reject(error);
            }
          );
        }
        return this.$q.reject(error);
      }
    );
  }

  private ensureToken(): IPromise<string> {
    if (this.token) {
      return this.$q.when(this.token);
    }
    return this.getTokenV2().then(token => {
      this.token = token;
      return token;
    });
  }

  public accountAccess(accountKey: string, userId?: string): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMerchantId());

        const request: any = {
          accountKey: accountKey,
          accountKeyType: 'Msisdn'
        };
        if (!!userId) {
          request.userId = userId;
        }

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.accountAccess(request, callback);
        }).then((response: any) => {
          if (response && response.result && response.result.cards) {
            response.result.cards = this.toV1MasterpassCards(response.result.cards);
          }
          return response;
        });
      });
    });
  }

  public addCard(request: any): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        try {
          const masterpass = (window as any)['Masterpass'];
          masterpass.setToken(token);
          masterpass.setMerchantId(this.getMerchantId());

          return this.wrapMasterpassCallback(callback => {
            masterpass.accountService.addCard({...request}, callback);
          });
        } catch (sdkError) {
          // SDK threw synchronous error - reject it so retry logic and controller can handle it
          const err = sdkError as any;
          return this.$q.reject({
            exception: {
              code: 'SDK_ERROR',
              message: err.message
            }
          });
        }
      });
    });
  }

  public verifyOtp(otpCode: string): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.verifyService.verifyOtp({
            otpCode: otpCode
          }, callback);
        });
      });
    });
  }

  public resendOtp(): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.verifyService.resendOtp({}, callback);
        });
      });
    });
  }

  public linkToMerchant(accountKey: string): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.linkToMerchant({
            accountKey: accountKey
          }, callback);
        });
      });
    });
  }

  public recurringOrder(request: any): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.recurringOrder(request, callback);
        });
      });
    });
  }

  public recurringOrderDelete(request: any): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.recurringOrderDelete(request, callback);
        });
      });
    });
  }

  public removeCard(accountKey: string, cardAlias: string): IPromise<any> {
    return this.callV2WithFreshToken<any>(token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.removeCard({
            accountKey: accountKey,
            cardAlias: cardAlias
          }, callback);
        });
      });
    });
  }

  public getToken(): IPromise<string> {
    return this.getVersion().then(version => {
      if (version === 'v2') {
        return this.getTokenV2();
      }

      return this.getTokenV1();
    });
  }

  private getTokenV1(): IPromise<string> {
    return this.httpService.extractResponseResult(
      this.$http.get(`hafifmuzik/mobile/masterpass/token`)
    );
  }

  private getTokenV2(): IPromise<string> {
    return this.getTokenV1();
  }

  public getPaymentMethodIdByListening(offerId: string): IPromise<Payment> {
    return this.httpService.getResultFromCache(`masterPackagePaymentMethod${offerId}`,
        `hafifmuzik/mobile/masterpass/getPaymentByOfferId?offerid=${offerId}`
    );
  }

  public getPaymentMethod(cardName: string, cardUid:string): IPromise<Payment> {
      return this.httpService.getResultFromCache(`masterPaymentMethods${cardName}`, `hafifmuzik/mobile/masterpass/getPayment?cardName=${cardName}&cardUid=${cardUid}`);
  }
  
  public startSubscription(
    packet: PackageThumbnail,
    cardName: string,
    cardUniqueId: string,
    promocode: string
  ): IPromise<Response> {
    return this.httpService.extractResponseResult(
      this.$http.post(
        `hafifmuzik/mobile/masterpass/startSubscription` + (!promocode ? `` : `?promocode=${promocode}`),
        {
          offerId: packet.id,
          cardName: cardName,
          cardUniqueId: cardUniqueId
        }
      ), true, true
    );
  }

  public updateSubscriptionMethod(cpcmOfferId: string, cardName: string, cardUniqueId: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.post(`hafifmuzik/mobile/masterpass/updateSubscriptionPayment`, {
        cpcmOfferId: cpcmOfferId,
        cardName: cardName,
        cardUniqueId: cardUniqueId
      }), true, true
    );
  }

  private isTokenExpiredError(error: any): boolean {
    return this.getMasterpassExceptionCode(error) === 'TOKEN_HAS_EXPIRED';
  }

  private getMasterpassExceptionCode(error: any): string {
    if (!error) {
      return null;
    }

    if (error.exception && error.exception.code) {
      return error.exception.code;
    }

    if (error.data && error.data.exception && error.data.exception.code) {
      return error.data.exception.code;
    }

    if (error.result && error.result.responseCode) {
      return error.result.responseCode;
    }

    if (error.data && error.data.result && error.data.result.responseCode) {
      return error.data.result.responseCode;
    }

    // Check for 401/403 HTTP status codes which indicate authentication failures
    if (error.statusCode === 401 || error.statusCode === 403) {
      return 'AUTHENTICATION_FAILED';
    }

    return null;
  }

  public toV1MasterpassCards(cards: any[]): any[] {
    if (!cards) {
      return [];
    }

    return cards.map(card => {
      return {
        BankIca: card.cardIssuerIcaNumber,
        CardStatus: card.cardState,
        EftCode: card.eftCode,
        IsMasterPassMember: card.isMasterpassMember,
        LoyaltyCode: null,
        Name: card.cardAlias,
        ProductName: card.productName,
        PromtCpin: null,
        UniqueId: card.uniqueCardNumber,
        Value1: card.maskedCardNumber,
        Value2: null,
        userListeningPackages: []
      };
    });
  }


  private getMerchantId(): string {
    return this.masterConfig.merchantId || this.masterConfig.clientId;
  }

  private wrapMasterpassCallback<T>(action: (callback: any) => void): IPromise<T> {
    const deferred = this.$q.defer<T>();
    const self = this;
    action(function(statusCode: any, responseBody: any) {
      const response = responseBody !== undefined ? responseBody : statusCode;
      const errorCode = self.getMasterpassExceptionCode(response);

      if (errorCode === 'TOKEN_HAS_EXPIRED' || errorCode === 'TOKEN_IS_NOT_VALID' || errorCode === 'AUTHENTICATION_FAILED') {
        deferred.reject(response);
        return;
      }
      deferred.resolve(response);
    });
    return deferred.promise;
  }
}



// WEBPACK FOOTER //
// ./src/app/buy/services/masterpass.service.ts